import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Select } from '@prio365/prio365-react-library';
import { makePrioStyles } from '../../theme/utils';

const useStyles = makePrioStyles((theme) => ({
  filtersContainer: {
    backgroundColor: theme.colors.application.background.default,
    borderRadius: theme.borderRadius.regular,
    border: `1px solid ${theme.colors.application.border}`,
    transition: 'all 0.3s ease',
  },
  filterHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.regular,
    borderBottom: `1px solid ${theme.colors.application.border}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.colors.application.background.hover,
    },
  },
  filterHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.small,
  },
  filterTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: theme.colors.application.typography.default,
  },
  filterActiveIndicator: {
    fontSize: '12px',
    color: theme.colors.base.primary.default,
    backgroundColor: theme.colors.base.primary.default + '20',
    padding: '2px 8px',
    borderRadius: theme.borderRadius.small,
    fontWeight: 500,
  },
  filterToggleIcon: {
    fontSize: '14px',
    color: theme.colors.application.typography.muted,
    transition: 'transform 0.3s ease',
  },
  filterToggleIconExpanded: {
    transform: 'rotate(180deg)',
  },
  filterContent: {
    padding: theme.spacing.regular,
  },
  filterRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: theme.spacing.regular,
    alignItems: 'end',
  },
  filterLabel: {
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: theme.spacing.small,
    color: theme.colors.application.typography.default,
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  clearFiltersButton: {
    justifySelf: 'start',
  },
  activeFiltersInfo: {
    fontSize: '12px',
    color: theme.colors.application.typography.muted,
    fontStyle: 'italic',
    marginTop: theme.spacing.small,
  },
  quickFilterChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.small,
    marginTop: theme.spacing.small,
  },
  quickFilterChip: {
    fontSize: '12px',
    padding: '4px 8px',
    backgroundColor: theme.colors.base.primary.default + '20',
    color: theme.colors.base.primary.default,
    borderRadius: theme.borderRadius.small,
    border: `1px solid ${theme.colors.base.primary.default}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.colors.base.primary.default + '30',
    },
  },
  collapsedFiltersPreview: {
    padding: theme.spacing.regular,
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.small,
    alignItems: 'center',
  },
  filterPreviewChip: {
    fontSize: '12px',
    padding: '4px 8px',
    backgroundColor: theme.colors.application.background.light,
    color: theme.colors.application.typography.default,
    borderRadius: theme.borderRadius.small,
    border: `1px solid ${theme.colors.application.border}`,
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  expandHint: {
    fontSize: '12px',
    color: theme.colors.application.typography.muted,
    fontStyle: 'italic',
  },
}));

interface CollapsibleFiltersProps {
  filters: {
    assignee?: string[];
    project?: string[];
    priority?: string[];
    labels?: string[];
    status?: string[];
    dueDate?: string[];
    createdBy?: string[];
  };
  filterOptions: {
    assignees: Array<{ value: string; label: string }>;
    projects: Array<{ value: string; label: string }>;
    priorities: Array<{ value: string; label: string }>;
    labels: Array<{ value: string; label: string }>;
    statuses: Array<{ value: string; label: string }>;
    dueDates: Array<{ value: string; label: string }>;
    createdBy: Array<{ value: string; label: string }>;
  };
  onFilterChange: (filterType: string, values: string[]) => void;
  onClearAllFilters: () => void;
  filtersLoading?: boolean;
  initiallyExpanded?: boolean;
}

const CollapsibleFilters: React.FC<CollapsibleFiltersProps> = ({
  filters,
  filterOptions,
  onFilterChange,
  onClearAllFilters,
  filtersLoading = false,
  initiallyExpanded = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some((filter) => filter && filter.length > 0);
  }, [filters]);

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    return Object.values(filters).filter(
      (filter) => filter && filter.length > 0
    ).length;
  }, [filters]);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);
  const filterOption = useCallback((inputValue: string, option: any) => {
    return option.label.toLowerCase().includes(inputValue.toLowerCase());
  }, []);

  return (
    <div className={classes.filtersContainer}>
      {/* Filter Header */}
      <div className={classes.filterHeaderRow} onClick={handleToggleExpand}>
        <div className={classes.filterHeaderLeft}>
          <span className={classes.filterTitle}>
            {t('taskHub:dashboard.filters.title')}
          </span>
          {hasActiveFilters && (
            <span className={classes.filterActiveIndicator}>
              {activeFiltersCount}{' '}
              {activeFiltersCount !== 1
                ? t('taskHub:dashboard.filters.filtersActive')
                : t('taskHub:dashboard.filters.filterActive')}
            </span>
          )}
        </div>
        <div
          className={`${classes.filterToggleIcon} ${
            isExpanded ? classes.filterToggleIconExpanded : ''
          }`}
        >
          <i className="fal fa-chevron-down" />
        </div>
      </div>

      {/* Expanded State Content */}
      {isExpanded && (
        <div className={classes.filterContent}>
          <div className={classes.filterRow}>
            <div className={classes.filterGroup}>
              <label className={classes.filterLabel}>
                {t('taskHub:dashboard.filters.project')}
              </label>
              <Select
                mode="multiple"
                placeholder={t('taskHub:dashboard.filters.allProjects')}
                value={filters.project || []}
                onChange={(values) => onFilterChange('project', values)}
                options={filterOptions.projects}
                allowClear
                style={{ width: '100%' }}
                loading={filtersLoading}
                showSearch
                filterOption={filterOption}
              />
            </div>

            <div className={classes.filterGroup}>
              <label className={classes.filterLabel}>
                {t('taskHub:dashboard.filters.assignee')}
              </label>
              <Select
                mode="multiple"
                placeholder={t('taskHub:dashboard.filters.allAssignees')}
                value={filters.assignee || []}
                onChange={(values) => onFilterChange('assignee', values)}
                options={filterOptions.assignees}
                allowClear
                style={{ width: '100%' }}
                loading={filtersLoading}
                showSearch
                filterOption={filterOption}
              />
            </div>

            <div className={classes.filterGroup}>
              <label className={classes.filterLabel}>
                {t('taskHub:dashboard.filters.createdBy')}
              </label>
              <Select
                mode="multiple"
                placeholder={t('taskHub:dashboard.filters.allCreators')}
                value={filters.createdBy || []}
                onChange={(values) => onFilterChange('createdBy', values)}
                options={filterOptions.createdBy}
                allowClear
                style={{ width: '100%' }}
                loading={filtersLoading}
                showSearch
                filterOption={filterOption}
              />
            </div>

            <div className={classes.filterGroup}>
              <label className={classes.filterLabel}>
                {t('taskHub:dashboard.filters.status')}
              </label>
              <Select
                mode="multiple"
                placeholder={t('taskHub:dashboard.filters.allStatuses')}
                value={filters.status || []}
                onChange={(values) => onFilterChange('status', values)}
                options={filterOptions.statuses}
                allowClear
                style={{ width: '100%' }}
                loading={filtersLoading}
                showSearch
                filterOption={filterOption}
              />
            </div>

            <div className={classes.filterGroup}>
              <label className={classes.filterLabel}>
                {t('taskHub:dashboard.filters.priority')}
              </label>
              <Select
                mode="multiple"
                placeholder={t('taskHub:dashboard.filters.allPriorities')}
                value={filters.priority || []}
                onChange={(values) => onFilterChange('priority', values)}
                options={filterOptions.priorities}
                allowClear
                style={{ width: '100%' }}
                loading={filtersLoading}
                showSearch
                filterOption={filterOption}
              />
            </div>

            <div className={classes.filterGroup}>
              <label className={classes.filterLabel}>
                {t('taskHub:dashboard.filters.labels')}
              </label>
              <Select
                mode="multiple"
                placeholder={t('taskHub:dashboard.filters.allLabels')}
                value={filters.labels || []}
                onChange={(values) => onFilterChange('labels', values)}
                options={filterOptions.labels}
                allowClear
                style={{ width: '100%' }}
                loading={filtersLoading}
                showSearch
                filterOption={filterOption}
              />
            </div>

            <div className={classes.filterGroup}>
              <label className={classes.filterLabel}>
                {t('taskHub:dashboard.filters.dueDate')}
              </label>
              <Select
                mode="multiple"
                placeholder={t('taskHub:dashboard.filters.allDueDates')}
                value={filters.dueDate || []}
                onChange={(values) => onFilterChange('dueDate', values)}
                options={filterOptions.dueDates}
                allowClear
                style={{ width: '100%' }}
                loading={filtersLoading}
                showSearch
                filterOption={filterOption}
              />
            </div>

            <div className={classes.clearFiltersButton}>
              <Button
                type="default"
                size="small"
                onClick={onClearAllFilters}
                iconProp={['fal', 'times']}
                disabled={!hasActiveFilters}
                loading={filtersLoading}
              >
                {t('taskHub:dashboard.filters.clearFilters')}
              </Button>
              {hasActiveFilters && (
                <div className={classes.activeFiltersInfo}>
                  {activeFiltersCount}{' '}
                  {activeFiltersCount !== 1
                    ? t('taskHub:dashboard.filters.filtersActive')
                    : t('taskHub:dashboard.filters.filterActive')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollapsibleFilters;
